import { Injectable } from '@angular/core';

import {
  checkBlacklist,
  checkNeverList,
  checkWhitelist,
  matchesList,
  FileValidationError,
} from '@digital-platform/shared/domain';

/**
 * Dummy file validator service to provide a dependency-free standin and
 * extensible interface for individual validator instances, use provider
 * overrides at the module or component level to provide your own extended
 * version of this class for your use case.
 *
 * Allowing both blacklisting and whitelisting allows for the most
 * flexibility in implementing the validator, a strict whitelist can
 * simply set mimeBlacklist to * and use a limited whitelist.
 * Additionally, an internal Never List prevents uploading of otherwise
 * harmful or dangerous files that could present infrastructure or other
 * security risks.
 */
@Injectable({
  providedIn: 'root',
})
export class FileValidatorService {
  protected defaultErrorMessage = 'The provided file is not allowed.';
  // mimeType or wildcard (e.g. application/*) and error message if matched
  protected mimeBlacklist: { [mimeType: string]: string } = {};
  // mimeType or wildcard (e.g. image/*)
  protected mimeWhitelist: string[] = [];

  public async validate(file: File): Promise<File> {
    if (!file) {
      throw new FileValidationError('No File Provided!');
    }

    const mimeType = file.type;

    // always fail on never list
    this.checkNeverList(mimeType);

    // always fail on white list
    this.checkWhitelist(mimeType);

    // check black list, and fail if not explicitly whitelisted
    try {
      this.checkBlacklist(mimeType);
    } catch (blacklistFail) {
      if (!matchesList(mimeType, this.mimeWhitelist)) {
        throw blacklistFail;
      }
    }

    return file;
  }

  protected checkBlacklist(mimeType: string) {
    return checkBlacklist(mimeType, this.mimeBlacklist);
  }

  protected checkWhitelist(mimeType: string) {
    return checkWhitelist(mimeType, this.mimeWhitelist, this.mimeBlacklist);
  }

  // these mimeTypes are never allowed past the validator and will always fail
  private checkNeverList(mimeType: string) {
    return checkNeverList(mimeType);
  }
}
